<template>
  <div class="w-full max-w-xl py-20 bg-white flex flex-col space-y-20">
    <button class="flex items-center cursor-pointer" @click="$router.back()">
      <i aria-hidden="true" class="fas fa-chevron-left text-sm" />
      <p class="mx-6">
        {{ $t('rewards.reward_confirmation.edit_selection') }}
      </p>
    </button>

    <h2>
      {{ $t('rewards.reward_confirmation.title') }}
    </h2>

    <Card v-if="basket.length > 0" class="p-30 space-y-20">
      <div v-if="redeemingMember.isDependent" class="mb-20">
        <p>
          {{
            $t('rewards.reward_confirmation.redeeming_for', [
              redeemingMember.name,
            ])
          }}
        </p>
      </div>

      <RewardBasketItem
        v-for="item in basketGroupedByReward"
        :key="`${item.memberCoverageId}-${item.rewardId}`"
        :item="item"
      />

      <div v-if="basket.length > 0" class="mt-20 space-y-20">
        <p class="font-semibold text-base border-t pt-20">
          {{ $t('rewards.reward_confirmation.sending_to') }}
        </p>

        <div class="space-y-20" :class="{ 'border-b pb-20': usesPoints }">
          <div v-if="containsDigitalGiftCard">
            <div>
              <fieldset>
                <legend class="font-semibold">
                  {{ $t('rewards.reward_confirmation.email') }}
                </legend>
                <FormRadioSelect
                  v-if="emailOptions.length > 0"
                  name="email"
                  v-model="emailCode"
                  :data="emailOptions"
                />
                <NoContactOnFile v-else />
              </fieldset>
            </div>
            <div>
              <button
                class="text-blue-500 underline font-semibold text-sm ml-10"
                @click="$emit('addEmail')"
              >
                {{ $t('rewards.send_to_different_email') }}
              </button>
            </div>
          </div>

          <p
            v-if="containsDigitalGiftCard"
            class="font-semibold text-base border-b pb-10"
          ></p>

          <div v-if="containsPhysicalGiftCard || containsMerchandise">
            <div>
              <fieldset>
                <legend class="font-semibold">
                  {{ $t('rewards.reward_confirmation.address') }}
                </legend>
                <FormRadioSelect
                  v-if="addressOptions.length > 0"
                  v-model="addressCode"
                  name="address"
                  :data="addressOptions"
                />
                <NoContactOnFile v-else />
              </fieldset>
            </div>
            <div>
              <button
                class="text-blue-500 underline font-semibold text-sm ml-10"
                @click="$emit('addAddress')"
              >
                {{ $t('rewards.send_to_different_address') }}
              </button>
            </div>
          </div>

          <p
            v-if="containsPhysicalGiftCard || containsMerchandise"
            class="font-semibold text-base border-b pb-10"
          ></p>

          <div class="text-grey-500 text-sm">
            {{
              coalescedSupportText(
                'common.contact_health_plan_csr.contact_info_change'
              )
            }}
          </div>
        </div>

        <div
          v-if="usesPoints && !healthActionCompletionId"
          class="rounded-md px-24 py-18 bg-grey-100 space-y-24"
        >
          <p class="font-semibold">
            {{ $t(`rewards.landing.current_balance.title.${currencyType}`) }}
          </p>
          <div class="h-1 bg-grey-300" />
          <div class="flex justify-between">
            <p>{{ $t('rewards.reward_confirmation.rewards_balance') }}</p>
            <p>{{ $t('rewards.points', [oldBalance]) }}</p>
          </div>
          <div class="flex justify-between">
            <p>{{ $t('rewards.reward_confirmation.to_be_redeemed') }}</p>
            <p>{{ $t('rewards.points', [redemptionAmount]) }}</p>
          </div>
          <div class="h-1 bg-grey-300" />
          <div class="flex justify-between">
            <p>{{ $t('rewards.reward_confirmation.remaining_balance') }}</p>
            <p>{{ $t('rewards.points', [newBalance]) }}</p>
          </div>
        </div>
      </div>
    </Card>
    <div v-else class="text-center border border-solid rounded-lg p-6 py-20">
      <i class="fas fa-shopping-cart text-xl" />
      <p>{{ $t('rewards.reward_confirmation.empty_cart') }}</p>
      <p>{{ $t('rewards.reward_confirmation.empty_cart_msg') }}</p>
    </div>

    <div class="mx-auto flex-shrink-0 space-y-20 flex flex-col justify-center">
      <HMButton :disabled="isDisabled" padding="wide" @click="placeOrder">
        {{ $t('rewards.reward_confirmation.confirm_btn') }}
      </HMButton>
      <HMButton color="white" outlined padding="wide" @click="onCancel()">
        {{ $t('common.cancel_btn') }}
      </HMButton>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _some from 'lodash/some'
import rewardsCurrency from '@platform-shared/mixins/rewardsCurrency'
import { Card } from '@components/common/card'
import supportMixin from '@components/mixins/support'
import FormRadioSelect from '@components/common/forms/FormRadioSelect'
import NoContactOnFile from '@components/views/rewards/delivery-destination/NoContactOnFile'
import RewardBasketItem from '@components/views/rewards/RewardBasketItem'

export default {
  components: {
    Card,
    FormRadioSelect,
    NoContactOnFile,
    RewardBasketItem,
  },
  mixins: [rewardsCurrency, supportMixin],
  data() {
    return {
      addressCode: null,
      emailCode: null,
      editInfoModal: false,
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters('rewards', [
      'availablePoints',
      'basket',
      'basketGroupedByReward',
      'deliveryAddress',
      'deliveryEmail',
      'memberForRedemption',
      'remainingMoney',
      'usesPoints',
    ]),
    ...mapGetters('member', [
      'addresses',
      'emails',
      'isFetching',
      'memberInfo',
    ]),
    containsPhysicalGiftCard() {
      return _some(this.basket, {
        reward: { deliveryForm: 'physical', type: 'GIFT_CARD' },
      })
    },
    containsDigitalGiftCard() {
      return _some(this.basket, {
        reward: { deliveryForm: 'digital', type: 'GIFT_CARD' },
      })
    },
    containsMerchandise() {
      return _some(this.basket, {
        reward: { deliveryForm: 'physical', type: 'MERCHANDISE' },
      })
    },
    healthActionCompletionId() {
      return this.$route.query.hacId
    },
    newBalance() {
      return this.remainingMoney(this.healthActionCompletionId)
    },
    oldBalance() {
      return this.availablePoints
    },
    redemptionAmount() {
      return -Math.abs(
        this.availablePoints -
          this.remainingMoney(this.healthActionCompletionId)
      )
    },
    redeemingMember() {
      return this.memberForRedemption()
    },
    addressOptions() {
      return this.addresses.map((x) => ({
        code: x.id.toString(),
        label: x.title,
        model: x,
      }))
    },
    emailOptions() {
      return this.emails.map((x) => ({
        code: x.id.toString(),
        label: x.address,
        model: x,
      }))
    },
    isDisabled() {
      return (
        this.isLoading ||
        !this.basket.length ||
        this.isFetching.verifyDeliveryDestinations
      )
    },
  },
  mounted() {
    this.addressCode = this.deliveryAddress?.id?.toString()
    this.emailCode = this.deliveryEmail?.id?.toString()
  },
  methods: {
    ...mapActions('rewards', [
      'initRewards',
      'invalidateRewards',
      'redeemCart',
      'resetBasket',
    ]),
    onSuccess(
      containsPhysicalGiftCard,
      containsDigitalGiftCard,
      containsMerchandise
    ) {
      this.invalidateRewards()
      this.initRewards()
      this.resetBasket()

      this.$router.push({
        name: 'rewards-success',
        params: {
          containsPhysicalGiftCard,
          containsDigitalGiftCard,
          containsMerchandise,
        },
      })
    },
    onCancel() {
      this.resetBasket()
      this.$router.push('/rewards')
    },
    placeOrder() {
      this.isLoading = true
      const {
        containsPhysicalGiftCard,
        containsDigitalGiftCard,
        containsMerchandise,
      } = this

      if (this.basket.length === 0) return

      const contactAddressId = this.addressOptions.find(
        (x) => x.code === this.addressCode
      )?.model?.id
      const contactEmailId = this.emailOptions.find(
        (x) => x.code === this.emailCode
      )?.model?.id

      this.redeemCart({
        deliveryDestination: {
          digital: contactEmailId,
          physical: contactAddressId,
        },
        healthActionCompletionId: this.healthActionCompletionId,
      })
        .then(() => {
          this.isLoading = false
          this.onSuccess(
            containsPhysicalGiftCard,
            containsDigitalGiftCard,
            containsMerchandise
          )
        })
        .catch(() => {
          this.isLoading = false
        })
    },
  },
}
</script>
<style></style>
